import React from "react"
import {
  Box, HStack, VStack, Text, Tooltip, UnorderedList, ListItem,
} from "@chakra-ui/react"
import {
  SheetFormatOption, SheetLayout, ExportFormat, FitStatus, getAveryCategory,
} from "components/BadgeBuilder/utils/sheetPresets"
import { formatMeasurement } from "components/BadgeBuilder/utils/units"
import { BadgeTemplate } from "sharedTypes/badge"
import { DioboxEvent } from "sharedTypes"
import { GuestData } from "components/BadgeBuilder/utils/badgeDataResolver"
import Pill from "components/elements/Pill"
import Icon from "components/elements/Icon"
import SheetPreviewDiagram from "./SheetPreviewDiagram"

type Props = {
  option: SheetFormatOption
  isSelected: boolean
  onClick: () => void
  sheet: SheetLayout | null
  grid: { columns: number; rows: number } | null
  fitStatus: FitStatus | null
  selectedBadge: BadgeTemplate | null
  previewGuests: GuestData[]
  event?: DioboxEvent
  selectedCount: number
  startPosition?: number
  onEditLayout?: () => void
}

const fmtIn = (mm: number) => formatMeasurement(mm, "in")

const SheetFormatCard = ({
  option, isSelected, onClick, sheet, grid, fitStatus,
  selectedBadge, previewGuests, event, selectedCount, startPosition, onEditLayout,
}: Props) => {
  const totalLabels = grid ? grid.columns * grid.rows : 0
  const averyCategory = getAveryCategory(option.format)

  const details: string[] = (() => {
    if (option.format === ExportFormat.SINGLE) {
      return [
        "Badge size matches template",
        "1 badge per page",
      ]
    }

    if (!sheet || !grid || grid.columns < 1 || grid.rows < 1) {
      return []
    }

    return [
      `Label: ${fmtIn(sheet.labelWidthMm)} × ${fmtIn(sheet.labelHeightMm)}`,
      `Page: ${fmtIn(sheet.pageWidthMm)} × ${fmtIn(sheet.pageHeightMm)}`,
      `${totalLabels} badges per sheet (${grid.columns} × ${grid.rows})`,
    ]
  })()

  const tooltipLabel = (() => {
    if (!fitStatus) return ""
    if (fitStatus === "perfect") return "Badge dimensions match the label size exactly."
    const badgeDims = selectedBadge
      ? `${fmtIn(selectedBadge.widthMm)} × ${fmtIn(selectedBadge.heightMm)}` : "?"
    const labelDims = sheet
      ? `${fmtIn(sheet.labelWidthMm)} × ${fmtIn(sheet.labelHeightMm)}` : "?"

    return `Badge (${badgeDims}) will be scaled to fit the label (${labelDims}).`
  })()

  return (
    <Box
      borderWidth={3}
      borderColor={isSelected ? "blue.500" : "transparent"}
      borderRadius="xl"
      w="full"
      cursor="pointer"
      transition="border-color 0.2s ease"
      _hover={{
        borderColor: isSelected ? "blue.600" : "blue.200",
      }}
      onClick={onClick}
    >
      <Box
        borderRadius="lg"
        overflow="hidden"
        borderWidth={1}
        borderColor="gray.200"
      >
        <HStack spacing={0}>
          {/* Preview area with gray background */}
          <Box
            bg={isSelected ? "blue.50" : "gray.50"}
            p={3}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            borderRightWidth={1}
            borderColor="gray.200"
          >
            <SheetPreviewDiagram
              columns={grid?.columns ?? null}
              rows={grid?.rows ?? null}
              format={option.format}
              sheet={sheet ?? null}
              selectedBadge={selectedBadge}
              previewGuests={previewGuests}
              event={event}
              selectedCount={selectedCount}
              startPosition={startPosition}
            />
          </Box>

          {/* Text content */}
          <Box flex={1} p={4}>
            <VStack align="start" spacing={2}>
              <Box>
                <Text fontSize="base" fontWeight="semibold">
                  {option.title}
                </Text>
                {averyCategory && (
                  <Tooltip
                    label={(
                      <VStack align="start" spacing={0.5} py={1}>
                        {averyCategory.compatibleTemplates.map((t) => (
                          <Text key={t.number}>
                            - Avery {t.number}, {t.description}
                          </Text>
                        ))}
                      </VStack>
                    )}
                    placement="top"
                    hasArrow
                    maxW="320px"
                  >
                    <HStack spacing={1} cursor="help">
                      <Text fontSize="sm" color="gray.800">
                        Compatible with {averyCategory.compatibleTemplates.length} Avery templates
                      </Text>
                      <Icon icon="info" size={3} color="gray.400" />
                    </HStack>
                  </Tooltip>
                )}
              </Box>
              {details.length > 0 && (
                <UnorderedList spacing={0} fontSize="sm" color="gray.800" sx={{ fontVariantNumeric: "tabular-nums" }} m={0} pl={4}>
                  {details.map((detail) => (
                    <ListItem key={detail}>{detail}</ListItem>
                  ))}
                </UnorderedList>
              )}
              {fitStatus && (
                <Tooltip label={tooltipLabel} placement="top" hasArrow maxW="240px">
                  <Box mt={1.5}>
                    <Pill
                      label={fitStatus === "perfect" ? "Perfect fit" : "Scales to fit"}
                      color="white"
                      borderWidth={1}
                      borderColor={fitStatus === "perfect" ? "green.500" : "orange.500"}
                      textColor={fitStatus === "perfect" ? "green.500" : "orange.500"}
                      width="auto"
                      px={2}
                    />
                  </Box>
                </Tooltip>
              )}
              {option.format === ExportFormat.CUSTOM_SHEET && !sheet && (
                <Text fontSize="xs" color="gray.600" mt={1.5}>{option.description}</Text>
              )}
              {option.format === ExportFormat.CUSTOM_SHEET && sheet && onEditLayout && (
                <Text
                  mt={1.5}
                  fontSize="xs"
                  color="blue.500"
                  fontWeight="semibold"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEditLayout()
                  }}
                  cursor="pointer"
                  _hover={{ textDecoration: "underline" }}
                >
                  Edit layout
                </Text>
              )}
            </VStack>
          </Box>
        </HStack>
      </Box>
    </Box>
  )
}

export default SheetFormatCard
